/**
 * Feb 2026 · 8 Sep 2026 · User-Directive
 *   „Das aktiv gewählte Objekt soll global im gesamten Kunden-Bereich
 *    synchronisiert sein. Wenn ein Kunde bei der Übersicht zwischen
 *    seinen Objekten wechselt (z.B. von Chalet Bergblick auf Villa
 *    Pfaff), sollen alle anderen Bereiche — insbesondere Einstellungen —
 *    automatisch auf dasselbe Objekt umschalten. Die Einstellungen
 *    sollen immer objektspezifisch sein und dem aktuell gewählten
 *    Objekt entsprechen."
 *
 * Globaler Property-Context für den Kunden-Bereich. Kapselt:
 *   · Laden der Objekte (`GET /properties`) einmalig beim Provider-Mount
 *   · 10-s-Poll, damit Team-Änderungen (neue Zuweisung, Löschung) live
 *     durchsickern
 *   · Serverseitige Persistenz der aktiven Auswahl (users.ui_prefs.selected_property_id)
 *   · Getter `propertiesById` als Convenience-Map
 *
 * Consumer-Seiten (Dashboard, Settings, Family, …) rufen `useProperty()` und
 * bekommen dasselbe `selected`-Objekt zurück — Wechsel auf einer Seite
 * spiegelt sich automatisch auf allen anderen.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "./api";
import { useAuth } from "./auth";

const PropertyContext = createContext(null);

export function PropertyProvider({ children }) {
  const { user, uiPrefs, setUiPrefs, resetUiPrefs } = useAuth();
  const [properties, setProperties] = useState(null);
  const [selected, setSelected] = useState(null);
  const storedIdRef = useRef(null);
  storedIdRef.current = uiPrefs?.selected_property_id || null;
  const pollRef = useRef(null);

  const applyProperties = useCallback((data) => {
    // Feb 2026 · 9 Sep 2026 · User-Directive „Hauptobjekt zuerst im
    // Switcher": Wir sortieren einmal zentral im Provider, damit JEDE
    // Consumer-View (PropertySwitcher, Dashboard, Handbook-Header,
    // Preferences-Copy-Dialog…) das Hauptobjekt an erster Stelle sieht.
    // Sekundärsortierung: chronologisch (created_at), damit Rest-Objekte
    // stabil bleiben.
    const sorted = [...(data || [])].sort((a, b) => {
      const ap = a?.is_primary ? 0 : 1;
      const bp = b?.is_primary ? 0 : 1;
      if (ap !== bp) return ap - bp;
      const ac = a?.created_at || "";
      const bc = b?.created_at || "";
      return ac.localeCompare(bc);
    });
    setProperties(sorted);
    setSelected((cur) => {
      if (!cur) {
        const stored = storedIdRef.current;
        if (stored) {
          const restored = sorted.find((x) => x.id === stored);
          if (restored) return restored;
        }
        return sorted[0] || null;
      }
      const fresh = sorted.find((x) => x.id === cur.id);
      return fresh || sorted[0] || null;
    });
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/properties");
      applyProperties(data || []);
    } catch (e) {
      console.debug("PropertyContext load failed:", e);
    }
  }, [applyProperties]);

  useEffect(() => {
    if (!user?.id) return;
    load();
    pollRef.current = setInterval(load, 10000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [user?.id, load]);

  // 23 Sep 2026 · Beim Wiederverbinden sofort frisch laden (Auto-Sync).
  useEffect(() => {
    const onReconnect = () => { load(); };
    window.addEventListener("wb:reconnect", onReconnect);
    return () => window.removeEventListener("wb:reconnect", onReconnect);
  }, [load]);

  const setSelectedProperty = useCallback((prop) => {
    setSelected(prop);
    if (prop?.id) setUiPrefs({ selected_property_id: prop.id });
  }, [setUiPrefs]);

  // Zurück auf Standard: Hauptobjekt (erstes nach Sortierung), Server-Pref löschen.
  const resetSelection = useCallback(async () => {
    setSelected((properties || [])[0] || null);
    await resetUiPrefs(["selected_property_id"]);
  }, [properties, resetUiPrefs]);

  const propertiesById = useMemo(() => {
    const map = {};
    (properties || []).forEach((p) => { map[p.id] = p; });
    return map;
  }, [properties]);

  const value = useMemo(() => ({
    properties: properties || [],
    loading: properties === null,
    selected,
    setSelectedProperty,
    resetSelection,
    propertiesById,
    reload: load,
  }), [properties, selected, setSelectedProperty, resetSelection, propertiesById, load]);

  return (
    <PropertyContext.Provider value={value}>
      {children}
    </PropertyContext.Provider>
  );
}

export function useProperty() {
  const ctx = useContext(PropertyContext);
  if (!ctx) {
    // Sicherheitsnetz: wenn eine Component außerhalb des Providers rendert,
    // liefern wir ein leeres Objekt zurück, damit die App nicht crasht.
    return { properties: [], loading: false, selected: null, setSelectedProperty: () => {}, resetSelection: async () => {}, propertiesById: {}, reload: () => {} };
  }
  return ctx;
}
